import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "fr-reservation" };
const _hoisted_2 = ["innerHTML"];
const _hoisted_3 = { class: "fr-free-tag" };
const _hoisted_4 = {
    key: 0,
    class: "fr-card-wrap"
};
const _hoisted_5 = {
    key: 0,
    class: "fr-panel fr-center fr-redirect"
};
const _hoisted_6 = /*#__PURE__*/ _createElementVNode("div", {
    class: "fr-redirect__spin",
    "aria-hidden": "true"
}, null, -1);
const _hoisted_7 = { class: "fr-hint" };
import StoreSearch from '@vue/components/interval/StoreSearch.vue';
import CustomerForm from '@vue/components/interval/CustomerForm.vue';
import OtpVerify from '@vue/components/interval/OtpVerify.vue';
import BookingDone from '@vue/components/interval/BookingDone.vue';
import NoticeModal from '@vue/components/interval/NoticeModal.vue';
import { checkDuplicateBooking, checkEligibility, reserving, sendOtp as sendOtpApi, unwrap, verifyOtp as verifyOtpApi, } from '@vue/services/interval-api';
import { isEn, setIntervalLang, t } from '@vue/services/interval-lang';
import { getRecaptchaToken, initRecaptcha } from '@vue/services/interval-recaptcha';
import { bookingState } from '@vue/services/interval-store';
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';
const ANALYTICS_WAIT_MS = 800;
/**
 * OTP 倒數：resendIn 是後端的重送冷卻（60 秒），expiresIn 是驗證碼有效期（300 秒）。
 * 兩個都以後端回應為準，前端不自己算秒數——後端調整常數時畫面要跟著對。
 */
const OTP_RESEND_FALLBACK = 60;
export default /*#__PURE__*/ _defineComponent({
    __name: 'IntervalReservationApp',
    props: {
        brandId: {},
        locale: {},
        recaptchaKey: {},
        bannerImage: {},
        heroImages: {},
        privacyUrl: {},
        brandNotice: {},
        announcementHtml: {}
    },
    setup(__props) {
        /**
         * 狂一鍋線上訂位（interval 佔用模式）。
         *
         * 由 prototype/booking.html 移植而來，流程與端點完全相同：
         *   搜尋門市時段 → 填資料 → OTP → 建單後直接前往訂位明細頁
         *
         * 與明水然（slot 模式）的 ReservationForm.vue 刻意分成兩套元件樹：
         * 兩者的可訂時段來源、餐點、訂金都不一樣，硬塞進同一個元件只會讓
         * 明水然那條已經穩定的路徑跟著一起冒風險。共用的是後端端點與國別碼清單。
         */
        const props = __props;
        /**
         * 訂位須知四欄的取用規則，與明水然系一致：
         * **優先讀該門市，門市該欄為空才回退品牌**。
         */
        function pickNotice(field) {
            const storeVal = field === 'reservationInstruction'
                ? bookingState.storeInstruction
                : bookingState.storeNotice;
            if ((storeVal || '').trim() !== '') {
                return storeVal;
            }
            const key = isEn() ? 'en' : 'tw';
            return props.brandNotice?.[key]?.[field] ?? '';
        }
        const noticeInstruction = computed(() => pickNotice('reservationInstruction'));
        const noticeReminder = computed(() => pickNotice('notice'));
        setIntervalLang(props.locale);
        // 門市卡片也要同一張圖，用 provide 傳下去，免得每層都再宣告一次 prop
        provide('bannerImage', props.bannerImage);
        provide('privacyUrl', props.privacyUrl);
        // 三張圖用 CSS 變數交給樣式的 media query 挑：inline style 蓋不掉斷點，
        // 且只有當下斷點用到的那張會被下載
        const bannerStyle = computed(() => {
            const images = props.heroImages;
            return {
                '--fr-hero-mobile': `url('${images?.mobile || props.bannerImage}')`,
                '--fr-hero-tablet': `url('${images?.tablet || props.bannerImage}')`,
                '--fr-hero-desktop': `url('${images?.desktop || props.bannerImage}')`,
            };
        });
        const sending = ref(false);
        /**
         * 建單成功後的導頁過場。
         *
         * 導頁不是瞬間發生的（要等埋點送出，瀏覽器也要跑一次完整載入），
         * 這段期間畫面若還停在驗證碼／填資料頁，客人會以為沒送出而重複點擊。
         */
        const redirecting = ref(false);
        /** 導頁前等埋點送出的上限毫秒數，逾時就走，不讓埋點拖住客人。 */
        const otpResendIn = ref(0);
        const otpExpiresIn = ref(0);
        let otpTimer = null;
        function stopOtpTimer() {
            if (otpTimer !== null) {
                window.clearInterval(otpTimer);
                otpTimer = null;
            }
        }
        /** 後端沒帶秒數時的保底冷卻，與 SmsOtpService::OTP_RESEND_SECONDS 一致。 */
        function startOtpCountdown(resendIn, expiresIn) {
            const resend = Number(resendIn);
            // 冷卻缺值就用保底：歸零會讓重送按鈕立刻可按，客人連按只是一直撞後端的
            // 60 秒冷卻、收一排「重新發送太過頻繁」。有效秒數則不保底——0 是合法狀態
            // （驗證碼真的過期了），保底反而會把「已失效」提示蓋掉。
            otpResendIn.value = resend > 0 ? Math.floor(resend) : OTP_RESEND_FALLBACK;
            otpExpiresIn.value = Math.max(0, Math.floor(Number(expiresIn) || 0));
            stopOtpTimer();
            otpTimer = window.setInterval(() => {
                if (otpResendIn.value > 0) {
                    otpResendIn.value--;
                }
                if (otpExpiresIn.value > 0) {
                    otpExpiresIn.value--;
                }
                if (otpResendIn.value <= 0 && otpExpiresIn.value <= 0) {
                    stopOtpTimer();
                }
            }, 1000);
        }
        onMounted(() => {
            initRecaptcha(props.recaptchaKey);
        });
        // 離開頁面（換頁／熱重載）時要收掉，否則計時器會一直跑
        onUnmounted(stopOtpTimer);
        function goStep(step) {
            bookingState.step = step;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        /**
         * 從驗證頁退回填資料頁。
         *
         * 倒數要一起收掉：客人回上一步通常是要改手機或姓名，改完重送等於
         * 走一輪新的 eligibility，舊的冷卻秒數留在畫面上只會誤導。
         */
        function backToForm() {
            stopOtpTimer();
            otpResendIn.value = 0;
            otpExpiresIn.value = 0;
            goStep(3);
        }
        /**
         * 步驟 3「發送驗證碼」。
         *
         * 先查資格：已驗證過的老客不必再收簡訊，直接建單；
         * 新客才依後端指定的方式（台灣簡訊、國外 Email）送驗證碼。
         */
        async function requestOtp() {
            const { countryCode, phone, email, name, date } = bookingState;
            sending.value = true;
            try {
                const eligibility = await checkEligibility(countryCode, phone, email, date, name);
                if (eligibility.blocked) {
                    // 後端把原因放在 blockMessage（「近期訂位次數過多，請聯繫門市」、
                    // 「操作過於頻繁，請稍後再試」、「此手機目前無法訂位」三種）。
                    // 舊版讀的是 message —— 那個鍵不存在，於是三種狀況一律顯示同一句
                    // 籠統的預設文案，客人看不懂、店家接到電話也問不出原因。
                    window.alert(eligibility.blockMessage || eligibility.message || t('bk.otpBlocked'));
                    return;
                }
                if (!eligibility.requiresVerification) {
                    await submitReservation();
                    return;
                }
                const type = eligibility.verificationType;
                if (type === 'captcha') {
                    window.alert(t('bk.otpCaptcha'));
                    return;
                }
                // reCAPTCHA 在 IP／未授權網域可能拿不到 token；不在前端硬擋，交由後端決定
                const token = await getRecaptchaToken();
                const sent = await sendOtpApi(type, countryCode, phone, email, date, token, name);
                // 後端判定免驗證（回 ok 但沒給 verificationId）→ 直接建單
                if (sent.ok && !sent.verificationId) {
                    await submitReservation();
                    return;
                }
                if (!sent.ok) {
                    window.alert(sent.message || t('bk.otpSendFail'));
                    return;
                }
                bookingState.verificationType =
                    sent.verificationType === 'email' || sent.verificationType === 'sms'
                        ? sent.verificationType
                        : type;
                bookingState.verificationId = sent.verificationId;
                bookingState.verifyDest =
                    bookingState.verificationType === 'email' ? email : `${countryCode} ${phone}`;
                startOtpCountdown(sent.resendIn, sent.expiresIn);
                goStep(4);
            }
            catch (e) {
                window.alert(e?.response ? t('bk.eligFail') : t('bk.networkFail'));
            }
            finally {
                sending.value = false;
            }
        }
        /**
         * 步驟 4「重新發送驗證碼」。
         *
         * 刻意不重跑 checkEligibility：重送本身會在 reservation_attempts 留下
         * stage=otp_send 的紀錄，再查一次資格會被自己灌高的計數翻成 captcha 或
         * blocked，回應就缺 verificationId／resendIn，畫面倒數歸零、剛寄出的
         * 驗證碼又對不到 session（明水然踩過這個坑）。後端 sendOtp 對「已有未過期
         * session」的手機也走同樣的短路，這裡直接打 sendOtp 與它對齊。
         * 真正的資格把關仍在最後 reserving 那一關。
         */
        async function resendOtp() {
            if (otpResendIn.value > 0 || sending.value) {
                return;
            }
            const { countryCode, phone, email, name, date } = bookingState;
            sending.value = true;
            try {
                const token = await getRecaptchaToken();
                const sent = await sendOtpApi(bookingState.verificationType, countryCode, phone, email, date, token, name, 
                // 後端靠這組 id 認出「同一次訂位流程的重寄」，才不會把碼已過期／已驗證過的
                // 重發當成新的一次訂位去重跑資格判定。
                bookingState.verificationId);
                if (!sent.ok) {
                    // 冷卻未過：後端會回剩餘秒數，用它把倒數接回去，
                    // 不然按鈕會停在可按狀態，客人連按只會一直收到同一句錯誤。
                    if (sent.code === 'OTP_RESEND_TOO_FREQUENT') {
                        startOtpCountdown(sent.retryAfter, otpExpiresIn.value);
                        window.alert(sent.message || t('bk.otpResendTooFrequent'));
                        return;
                    }
                    // blocked 的原因在 blockMessage（message 這個鍵不存在），讀錯會把
                    // 「訂位已達上限」之類的具體理由蓋成一句籠統的發送失敗。
                    // 其餘失敗一律附上自救指引：重發在這一頁已經走不通，退回上一步重送才有用。
                    window.alert((sent.blockMessage || sent.message || t('bk.otpSendFail'))
                        + '\n' + t('bk.otpResendFallbackHint'));
                    return;
                }
                // 後端判定此人已不需驗證（回 ok 但沒給 verificationId）→ 直接建單。
                // 少了這段會顯示「已重新發送」卻其實沒寄，倒數又缺值歸零而顯示「驗證碼已失效」，
                // 客人手上沒有任何有效的碼可輸入。requestOtp 走的是同一套判斷。
                if (!sent.verificationId) {
                    await submitReservation();
                    return;
                }
                // 簡訊發送失敗時後端會自動改寄 Email，verificationType 必須跟著切，
                // 否則驗證碼會打到 verifyOtp（簡訊端點）而永遠對不上。
                if (sent.verificationType === 'email' || sent.verificationType === 'sms') {
                    bookingState.verificationType = sent.verificationType;
                }
                bookingState.verificationId = sent.verificationId;
                bookingState.verifyDest =
                    bookingState.verificationType === 'email' ? email : `${countryCode} ${phone}`;
                startOtpCountdown(sent.resendIn, sent.expiresIn);
                window.alert(t('bk.otpResent'));
            }
            catch (e) {
                window.alert(t('bk.otpSendFail'));
            }
            finally {
                sending.value = false;
            }
        }
        /** 步驟 4「確認訂位」：先驗 OTP，過了才真正建單。 */
        async function verifyAndReserve(otp) {
            if (otp.length < 6) {
                window.alert(t('bk.otpIncomplete'));
                return;
            }
            sending.value = true;
            try {
                const verified = await verifyOtpApi(bookingState.verificationType, bookingState.verificationId, bookingState.countryCode, bookingState.phone, bookingState.email, otp);
                if (!verified.ok) {
                    window.alert(verified.message || t('bk.otpVerifyFail'));
                    return;
                }
                bookingState.otpVerifiedToken = verified.otpVerifiedToken || '';
                await submitReservation();
            }
            catch (e) {
                window.alert(e?.response ? t('bk.otpVerifyFail') : t('bk.networkFail'));
            }
            finally {
                sending.value = false;
            }
        }
        /** 建單。interval 分支會配桌、寫佔用、產短網址、寄簡訊與 Email。 */
        async function submitReservation() {
            if (!bookingState.slot) {
                window.alert(t('bk.alertNoSlot'));
                return;
            }
            // 重複訂位提前擋下。後端 reserving 仍會再擋一次，這裡只是不要讓客人
            // 填完整份表單、收完簡訊，最後才在送出時被打回。
            try {
                const duplicated = await checkDuplicateBooking(bookingState.countryCode, bookingState.phone, bookingState.storeId, bookingState.date);
                if (duplicated) {
                    window.alert(t('bk.dupBooking'));
                    return;
                }
            }
            catch (e) {
                // 查詢失敗不阻斷：後端仍會把關，不因這道提示掛掉就卡住客人
            }
            // reserving 的 interval 分支以 date 當用餐起點，
            // 要帶「店家台北牆上時間」的全時間字串，不可讓瀏覽器時區介入。
            const startAt = `${bookingState.date}T${bookingState.slot.time}:00+08:00`;
            const token = await getRecaptchaToken();
            try {
                const json = await reserving({
                    storeId: bookingState.storeId,
                    date: startAt,
                    adult: bookingState.adult,
                    child: bookingState.child,
                    childSeat: bookingState.childSeat,
                    name: bookingState.name.trim(),
                    gender: bookingState.gender,
                    phone: bookingState.phone.trim(),
                    email: bookingState.email.trim(),
                    countryCode: bookingState.countryCode,
                    purpose: bookingState.purpose,
                    customerNote: bookingState.note || '',
                    gRecaptchaResponse: token,
                    otpVerifiedToken: bookingState.otpVerifiedToken || '',
                });
                if (!json.success) {
                    window.alert(t('bk.alertFailPrefix') + (json.message || t('bk.slotFail')));
                    return;
                }
                const out = unwrap(json, ['order', 'orderShortUrl']);
                bookingState.orderNo = out.order?.no || '';
                bookingState.orderShortUrl = out.orderShortUrl || '';
                stopOtpTimer();
                // 只認 http(s) 絕對網址：正常情況後端給的就是短網址服務的完整網址，
                // 拿到別的東西（空字串、相對路徑）一律當作沒短網址走保底完成頁。
                const target = /^https?:\/\//i.test(bookingState.orderShortUrl)
                    ? bookingState.orderShortUrl
                    : '';
                // 過場先蓋上：等埋點送出的這段時間畫面不能還停在驗證碼頁
                if (target) {
                    redirecting.value = true;
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                await trackBookingSuccess(Number(out.order?.total ?? 0));
                // 建單完成直接前往訂位明細（與簡訊／Email 內是同一組短網址），不再多一頁完成畫面。
                // 用 replace 不用 assign：完成後按上一頁會回到已送出的表單，客人再按一次送出
                // 就是一筆重複訂位（後端會擋，但客人只會看到一句錯誤訊息，體驗很差）。
                if (target) {
                    window.location.replace(target);
                    return;
                }
                // 保底：短網址沒產出來（產碼異常）時仍留在原本的完成畫面。
                // 這裡不可改成「導向首頁」或什麼都不做——訂單已經建立，
                // 至少要讓客人看到訂位編號，否則他會以為沒訂到而再訂一次。
                goStep(5);
            }
            catch (e) {
                // 沒拿到回應（逾時、斷網）＝請求可能已到後端、單也建好了，只是回應沒回來。
                // 說「訂位失敗」客人會直接重訂而變成重複訂位，要請他先確認（2026-10-13 驗收報告第 4 節）。
                window.alert(e?.response ? t('bk.alertFail') : t('bk.alertUncertain'));
            }
        }
        /**
         * 訂位成功埋點（GA4 + Meta Pixel + TikTok Pixel，欄位與明水然系 booking_success 同口徑）。
         *
         * 回傳的 Promise 在 GA4 回報送出（event_callback）或逾時後 resolve：
         * 建單完成後會立刻導頁，導頁會中斷還沒送完的請求，漏掉的就是轉換數字。
         * set_menu 恆空：本品牌現場點餐不選餐點（reserving 的 meals 本來就是空陣列）。
         */
        function trackBookingSuccess(total) {
            return new Promise((resolve) => {
                let settled = false;
                const done = () => {
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                };
                // 逾時兜底：埋點不論成功與否都不可把客人卡在過場畫面
                window.setTimeout(done, ANALYTICS_WAIT_MS);
                try {
                    const brand = 'twfondueretro';
                    const orderNo = bookingState.orderNo;
                    const storeName = bookingState.storeName || '';
                    // 前端第一層防重：同一訂位編號在本 session 只送一次。
                    // 事件掛在這個「後端已回訂位編號」的位置，絕不可改用 URL／page_view 判斷轉換
                    // （導頁後的訂位明細頁是主站的頁面，重新整理就會多算一次）。
                    // sessionStorage 攔不到跨裝置／清快取的重複，故三個平台一律帶訂位編號當去重鍵
                    // （Meta 的 eventID、TikTok 的 event_id、GA4 的 transaction_id），由平台端兜底。
                    let alreadySent = false;
                    try {
                        if (orderNo) {
                            alreadySent = sessionStorage.getItem('bs_' + orderNo) === '1';
                            sessionStorage.setItem('bs_' + orderNo, '1');
                        }
                    }
                    catch (e) {
                        // 隱私模式等情境下 sessionStorage 不可用：放行送出，重複交給平台端去重
                    }
                    const analyticsPayload = {
                        brand: brand,
                        store: storeName,
                        party_size: (bookingState.adult || 0) + (bookingState.child || 0),
                        booking_date: bookingState.date || '',
                        set_menu: '',
                        value: total,
                        currency: 'TWD',
                        order_no: orderNo,
                    };
                    // Meta Pixel：標準事件 Purchase（與明水然系同口徑）。
                    // 2026-08-10：行銷部確認廣告帳號的轉換最佳化目標設在標準 Purchase，
                    //    自訂事件（booking_success）收不到轉換訊號，故由 trackCustom 改回 track/Purchase。
                    // ⚠️ 此處不可再改成 trackCustom；GA4 維持 booking_success、TikTok 維持 PlaceAnOrder。
                    // value 一律帶實際金額（行銷部代碼包寫死 0 會讓 ROAS 算不出來），eventID 帶訂位編號去重。
                    const fbq = window.fbq;
                    if (!alreadySent && typeof fbq === 'function') {
                        fbq('track', 'Purchase', analyticsPayload, orderNo ? { eventID: orderNo } : undefined);
                    }
                    // TikTok Pixel：標準事件 PlaceAnOrder（行銷部指定），event_id 帶訂位編號去重
                    const ttq = window.ttq;
                    if (!alreadySent && ttq && typeof ttq.track === 'function') {
                        ttq.track('PlaceAnOrder', {
                            content_id: brand + '_' + storeName,
                            content_name: storeName,
                            currency: 'TWD',
                            value: analyticsPayload.value,
                        }, orderNo ? { event_id: orderNo } : undefined);
                    }
                    const gtag = window.gtag;
                    if (!alreadySent && typeof gtag === 'function') {
                        gtag('event', 'booking_success', {
                            ...analyticsPayload,
                            transaction_id: orderNo,
                            // 送達（或 gtag 判定送不出去）就提早結束等待，多數情況只等幾十毫秒
                            event_callback: done,
                        });
                    }
                    else {
                        done();
                    }
                }
                catch (e) {
                    // 埋點失敗一律不影響訂位流程
                    done();
                }
            });
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("section", {
                    class: "fr-banner",
                    style: _normalizeStyle(bannerStyle.value)
                }, [
                    _createElementVNode("h1", null, _toDisplayString(_unref(t)('bk.title')), 1),
                    _createElementVNode("p", null, _toDisplayString(_unref(t)('bk.subtitle')), 1),
                    (props.announcementHtml)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "fr-announcement",
                            innerHTML: props.announcementHtml
                        }, null, 8, _hoisted_2))
                        : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('bk.free')), 1)
                ], 4),
                _withDirectives(_createVNode(StoreSearch, {
                    "brand-id": props.brandId,
                    onSelectSlot: _cache[0] || (_cache[0] = ($event) => (goStep(3)))
                }, null, 8, ["brand-id"]), [
                    [_vShow, _unref(bookingState).step === 1]
                ]),
                (_unref(bookingState).step !== 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        (redirecting.value)
                            ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
                                _hoisted_6,
                                _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)('bk.redirecting')), 1)
                            ]))
                            : (_unref(bookingState).step === 3)
                                ? (_openBlock(), _createBlock(CustomerForm, {
                                    key: 1,
                                    sending: sending.value,
                                    onBack: _cache[1] || (_cache[1] = ($event) => (goStep(1))),
                                    onSubmit: requestOtp
                                }, null, 8, ["sending"]))
                                : (_unref(bookingState).step === 4)
                                    ? (_openBlock(), _createBlock(OtpVerify, {
                                        key: 2,
                                        sending: sending.value,
                                        "resend-in": otpResendIn.value,
                                        "expires-in": otpExpiresIn.value,
                                        onBack: backToForm,
                                        onResend: resendOtp,
                                        onVerify: verifyAndReserve
                                    }, null, 8, ["sending", "resend-in", "expires-in"]))
                                    : (_unref(bookingState).step === 5)
                                        ? (_openBlock(), _createBlock(BookingDone, { key: 3 }))
                                        : _createCommentVNode("", true)
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(NoticeModal, {
                    instruction: noticeInstruction.value,
                    notice: noticeReminder.value,
                    active: _unref(bookingState).step === 3
                }, null, 8, ["instruction", "notice", "active"])
            ]));
        };
    }
});
